function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { useAutocomplete } from '@mui/base/useAutocomplete';
import { useForkRef } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedPopover, HighlightText, lineClampStyle, notchedPopperModifier, sameWidthPopperModifier } from '@arc/frontend/ui-common';
import { theme, typography } from '@arc/frontend/ui-theme';
import { selectorStyles } from '../Selectors';
import { limitGroupedOptions, limitOptions } from '../use-autocomplete-helpers/limit-options';
import { hubAutocompleteDefaultRenderInput } from './HubAutocompleteRenderInput';
/**
 * - Select a single option from a list of options.
 * - The options can be filtered by typing in the input.
 */
export function HubAutocomplete({
  // HubAutocompleteRenderInputProps
  error,
  archived,
  readOnly,
  disabled,
  inputRef,
  placeholder,
  startAdornment,
  endAdornment,
  // UseAutocompleteProps
  options,
  getOptionLabel = option => '' + option,
  getExample,
  getOptionDisabled,
  isOptionEqualToValue,
  value,
  onChange,
  inputValue: propInputValue,
  onInputChange,
  open,
  onOpen,
  onClose,
  filterOptions,
  groupBy,
  disableClearable,
  freeSolo,
  blurOnSelect,
  autoComplete,
  autoSelect,
  autoHighlight,
  // other
  popoverRef,
  testid,
  maxListSize = 50,
  canSearchForMore,
  isLoading,
  className,
  styles,
  slotProps,
  getOptionKey,
  renderInput = hubAutocompleteDefaultRenderInput,
  renderGroup = hubAutocompleteDefaultRenderGroup,
  renderOption = hubAutocompleteDefaultRenderOption,
  hideDropdownWhenEmpty = false,
  emptyMessage,
  dropdownTopContainer,
  dropdownBottomContainer,
  popperProps = HubAutocompleteDefaultPopperProps
}) {
  const {
    t
  } = useTranslation();
  const [inputUnchangedSinceOpen, setInputUnchangedSinceOpen] = useState(false);
  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    anchorEl,
    setAnchorEl,
    popupOpen: _popupOpen,
    getPopupIndicatorProps,
    getClearProps,
    inputValue,
    focused
  } = useAutocomplete({
    options,
    getOptionLabel: (...args) => getOptionLabel(...args) ?? '',
    getOptionDisabled,
    isOptionEqualToValue,
    value,
    onChange: (_event, newValue) => {
      onChange(newValue);
    },
    disabled,
    // Prevent popover from opening when the input is read-only.
    open: readOnly ? false : open,
    onOpen: event => {
      setInputUnchangedSinceOpen(true);
      onOpen?.(event);
    },
    onClose,
    inputValue: propInputValue,
    onInputChange: (event, newValue, reason) => {
      setInputUnchangedSinceOpen(false);
      onInputChange?.(event, newValue, reason);
    },
    filterOptions,
    groupBy,
    blurOnSelect,
    disableClearable,
    freeSolo,
    autoComplete,
    autoSelect,
    autoHighlight
  });
  const renderLimitedNotice = () => <li css={selectorStyles.emptyListItem}>{t('typeForMoreResults')}</li>;
  const renderOptionInternal = (option, index) => {
    let optionProps = getOptionProps({
      option,
      index
    });
    if (getOptionDisabled?.(option)) {
      optionProps = {
        ...optionProps,
        onClick: undefined
      };
    }
    return renderOption({
      optionProps,
      label: getOptionLabel(option),
      option,
      inputValue: inputUnchangedSinceOpen ? '' : inputValue,
      // Material UI adds a `key` prop to the option props, but its type declaration is wrong.
      key: getOptionKey?.(option) ?? optionProps.key,
      example: getExample?.(option)
    });
  };
  const renderOptions = options_ => options_.map((option, index) => renderOptionInternal(option, index));
  const renderGroups = groups => groups.map(({
    key,
    group,
    options: options_,
    index
  }) => renderGroup({
    key,
    group,
    children: options_.map((option2, index2) => renderOptionInternal(option2, index + index2))
  }));
  const popupOpen = _popupOpen && (groupedOptions.length > 0 || !hideDropdownWhenEmpty);
  const listboxProps = getListboxProps();
  const listboxRef = useForkRef(
  // @ts-expect-error `ref` is not in the type definition.
  listboxProps?.ref, slotProps?.listbox?.ref);
  return <div {...getRootProps()} {...slotProps?.root} className={className} css={styles?.root}>
			{renderInput({
      error,
      archived,
      readOnly,
      disabled,
      inputRef,
      placeholder,
      startAdornment,
      endAdornment,
      disableClearable,
      inputProps: {
        ...getInputProps(),
        ...slotProps?.input
      },
      setAnchorEl,
      getClearProps,
      getPopupIndicatorProps,
      popupOpen,
      focused,
      inputValue,
      styles,
      testid
    })}
			<AnimatedPopover ref={popoverRef} anchorEl={anchorEl} open={popupOpen} PopperProps={popperProps} css={selectorStyles.popover()} data-testid={`${testid}-popover`}>
				<ul {...listboxProps} {...slotProps?.listbox} ref={listboxRef} css={[selectorStyles.list, "max-height:40vh;overflow:auto;", styles?.listbox, process.env.NODE_ENV === "production" ? "" : ";label:HubAutocomplete;"]}>
					{dropdownTopContainer}

					{groupedOptions.length <= 0 ? isLoading ? <li css={[selectorStyles.emptyListItem, typography.emptySmall, process.env.NODE_ENV === "production" ? "" : ";label:HubAutocomplete;"]}>
								{t('loading')}
							</li> : <li css={selectorStyles.emptyListItem}>
								{emptyMessage ?? t('noResults')}
							</li> : groupBy ? (() => {
          const {
            limitedGroups,
            limited
          } = limitGroupedOptions(groupedOptions, maxListSize);
          return <>
									{renderGroups(limitedGroups)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>;
        })() : (() => {
          const {
            limitedOptions,
            limited
          } = limitOptions(groupedOptions, maxListSize);
          return <>
									{renderOptions(limitedOptions)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>;
        })()}

					{dropdownBottomContainer}
				</ul>
			</AnimatedPopover>
		</div>;
}
export const HubAutocompleteBasePopperProps = {
  placement: 'bottom-start',
  modifiers: [{
    name: 'offset',
    options: {
      offset: [0, -2]
    }
  }, notchedPopperModifier({
    notchDepth: 1
  })]
};
export const HubAutocompleteDefaultPopperProps = {
  ...HubAutocompleteBasePopperProps,
  modifiers: [...(HubAutocompleteBasePopperProps.modifiers ?? []), sameWidthPopperModifier]
};
var _ref = process.env.NODE_ENV === "production" ? {
  name: "8m89z8",
  styles: "& > li{padding-left:1.5rem;}"
} : {
  name: "1y35zm4-hubAutocompleteDefaultRenderGroup",
  styles: "& > li{padding-left:1.5rem;};label:hubAutocompleteDefaultRenderGroup;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
export function hubAutocompleteDefaultRenderGroup({
  key,
  group,
  children
}) {
  return <li key={key}>
			<div css={[typography.regular, {
      color: theme.colors.text.secondary,
      padding: '0.5rem 0.75rem'
    }, process.env.NODE_ENV === "production" ? "" : ";label:hubAutocompleteDefaultRenderGroup;"]}>
				{group}
			</div>
			<ul css={_ref}>{children}</ul>
		</li>;
}
export function hubAutocompleteDefaultRenderOption({
  optionProps,
  label,
  inputValue,
  key,
  example
}) {
  return <li {...optionProps} key={key} css={selectorStyles.listItem}>
			<HighlightText text={label} highlight={inputValue} lineClamp />
			{example ? <div css={[{
      color: theme.colors.text.faint,
      marginLeft: '0.75rem'
    }, lineClampStyle(1), process.env.NODE_ENV === "production" ? "" : ";label:hubAutocompleteDefaultRenderOption;"]}>
					{example}
				</div> : null}
		</li>;
}